import { HttpClient } from '@angular/common/http';
import { Injectable, effect, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NewFeaturesComponent } from 'src/app/shared/components/new-features/new-features.component';
import { AuthUserStateService } from '../state/auth-user-state.service';
import { LanguageService } from './language.service';

export interface NewFeaturesDialogData {
    language: string;
    version: string;
    features: any[];
    modalTitle: any;
    executeAction?: (actionName: string) => void;
}

@Injectable({
    providedIn: 'root'
})
export class NewFeaturesService {
    // Section: SERVICES / DEPENDENCIES
    //----------------------------------------------------------------------
    private readonly http = inject(HttpClient);
    private readonly dialog = inject(MatDialog);
    private readonly languageService = inject(LanguageService);
    private readonly authUserStateService = inject(AuthUserStateService);

    // Section: PRIVATE PROPERTIES
    //----------------------------------------------------------------------
    // Un diccionario de acciones reutilizables e independientes
    private readonly actionDictionary: Record<string, () => void> = {
        openDemo: () => {
            if (typeof Supademo !== 'undefined') {
                Supademo.open('cmr1yf9zf01jpz40jq8y2ttkj');
            } else {
                console.warn('Supademo library is not loaded yet in the document body.');
            }
        }
    };

    constructor() {
        effect(() => {
            if (this.authUserStateService.isAuthenticated()) {
                this.checkForNewFeaturesAdvice();
            }
        });
    }

    // Section: PUBLIC METHODS
    //----------------------------------------------------------------------
    checkForNewFeaturesAdvice(eventSource?: string): void {
        this.http.get('/newFeatures.json').subscribe({
            next: (response: any) => {
                if (!response || !response.version) {
                    return;
                }
                const currentVersion = response.version;
                const userLanguage = this.languageService.getCurrentLanguage();

                // Delay execution by 500ms to allow layout and resources to load/settle
                setTimeout(() => {
                    let whatsNew: string[] = [];
                    try {
                        const saved = localStorage.getItem('whatsNew');
                        if (saved) {
                            whatsNew = JSON.parse(saved);
                        }
                    } catch (e) {
                        console.error('Failed to parse whatsNew from localStorage', e);
                    }

                    if (!Array.isArray(whatsNew)) {
                        whatsNew = [];
                    }

                    // Show modal if the current version is new, or if explicitly opened from the popover
                    if (!whatsNew.includes(currentVersion) || eventSource === 'popover') {
                        this.showNewFeaturesModalAdvice(userLanguage, currentVersion, response.features, response.modalTitle);
                    }
                }, 500);
            },
            error: () => {}
        });
    }

    executeAction(actionName: string): void {
        const action = this.actionDictionary[actionName];
        if (action) {
            action();
        } else {
            console.warn(`Action "${actionName}" was not found in the NewFeaturesService action dictionary.`);
        }
    }

    // Section: PRIVATE METHODS
    //----------------------------------------------------------------------
    private showNewFeaturesModalAdvice(userLanguage: string, version: string, features: any[], modalTitle: any): void {
        const dialogSettings = new MatDialogConfig();
        dialogSettings.height = '505px';
        dialogSettings.width = '900px';
        dialogSettings.position = { top: '20vh' };
        dialogSettings.panelClass = ['animated', 'slideInDown', 'new-features'];
        dialogSettings.data = {
            language: userLanguage,
            version: version,
            features: features,
            modalTitle: modalTitle,
            executeAction: (actionName: string) => {
                this.executeAction(actionName);
            }
        };

        this.dialog
            .open(NewFeaturesComponent, dialogSettings)
            .afterClosed()
            .subscribe((result) => {
                if (result && result.closed) {
                    let whatsNew: string[] = [];
                    try {
                        const saved = localStorage.getItem('whatsNew');
                        if (saved) {
                            whatsNew = JSON.parse(saved);
                        }
                    } catch (e) {
                        // ignore
                    }
                    if (!Array.isArray(whatsNew)) {
                        whatsNew = [];
                    }
                    if (!whatsNew.includes(version)) {
                        whatsNew.unshift(version);
                    }
                    localStorage.setItem('whatsNew', JSON.stringify(whatsNew));
                }
            });
    }
}
